import { useState } from "react";

import { SpotlightSearch } from "@components/CasesList/components/SpotlightSearch";
import { PageHeader } from "@components/PageHeader";
import { Accordion } from "@components/ui/accordion";
import { Button } from "@components/ui/button";

import { useTemplates } from "@hooks/queries/use-templates";

import { cn } from "@utils/ui";

import { CreateTemplateModal } from "./components/CreateTemplateModal";
import { TemplateCard } from "./components/TemplateCard";
import { TemplateCardSkeleton } from "./components/TemplateCardSkeleton";
import { TEMPLATE_LIST_GRID_COLS } from "./constants/columns";

const ReportTemplatesPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data: templates = [], isLoading } = useTemplates();

  const filteredTemplates = templates.filter((template) =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-2 p-2 pl-0">
      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white p-4">
        <PageHeader
          title="Report Templates"
          description="Manage and create report templates for your cases."
          rightContent={
            <Button size="sm" onClick={() => setIsCreateModalOpen(true)}>
              Create New Template
            </Button>
          }
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-foreground-muted/20 bg-background-white">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <h2 className="text-base font-semibold tracking-[-0.01em]">Templates</h2>
          <div className="w-[320px] shrink-0">
            <SpotlightSearch
              placeholder="Search templates here..."
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>

        {isLoading ? (
          <div>
            {Array.from({ length: 3 }).map((_, index) => (
              <TemplateCardSkeleton key={index} />
            ))}
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">No templates found</div>
          </div>
        ) : (
          <>
            <div className="flex h-10 items-center gap-4 border-b border-border bg-background-muted/50 px-4">
              <div
                className={cn(
                  "grid flex-1 gap-4 font-head text-xs font-semibold text-foreground-muted",
                  TEMPLATE_LIST_GRID_COLS
                )}
              >
                <span>Template Name</span>
                <span>Sections</span>
                <span>Created</span>
              </div>
              {/* Spacer matching AccordionTrigger's chevron icon so the labels above line up with the row values. */}
              <div className="size-4 shrink-0" />
            </div>

            <Accordion type="multiple">
              {filteredTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  allTemplates={templates}
                />
              ))}
            </Accordion>
          </>
        )}
      </div>

      <CreateTemplateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        templates={templates}
      />
    </div>
  );
};

export default ReportTemplatesPage;
